import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import urlJoin from 'url-join';

import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { TokenExchangeResponse } from '../../../features/ai-hub/models/token.types';
import { jwtDecode } from 'jwt-decode';

/**
 * Root base class for all HTTP services.
 * Owns http, impersonation state, token access, and auth readiness check.
 * All subclasses inherit these automatically — never re-implement them.
 */
export abstract class BaseHttpService {

  /** Base API URL — set by each concrete service from config. */
  public baseUrl: string = '';

  /** API subscription key — set by each concrete service from config. */
  protected apiKey: string = '';

  public helpUrl: string = '';

  constructor(
    protected http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,
    protected impersonation: ImpersonationService,
    protected oktaWrapper: OktaWrapperService
  ) {

    const configSettings = config['general'];
    this.baseUrl = configSettings['adminApiUrl'];
    this.apiKey = configSettings['apiKey'];
    this.helpUrl = configSettings['helpUrl'];
  }

  // -------------------- Auth --------------------

  /**
   * Returns the current access token via OktaWrapperService.
   * Single token access point for all services — never override this.
   */
  public getToken(): string | undefined {
    return this.oktaWrapper.getAccessToken();
  }

  // Mirrors the BFF token-exchange contract using the Okta access token already held.
  public getInternalToken(): Observable<TokenExchangeResponse> {

    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('Security check failed'));
    }

    const expiresAtSeconds = jwtDecode<{ exp?: number }>(token).exp ?? 0;
    const expiresIn = Math.max(0, expiresAtSeconds - Math.floor(Date.now() / 1000));

    return of({
      token,
      tokenType: 'Bearer',
      expiresAt: new Date(expiresAtSeconds * 1000).toISOString(),
      expiresIn
    });
  }

  /**
   * Returns true only when Okta confirms authenticated AND a valid token is held.
   * Always use this before making API calls — covers all timing scenarios including
   * browser reopen, token renewal windows, and first load.
   */
  public async isReadyToUse(): Promise<boolean> {
    const isAuth = await this.oktaWrapper.isAuthenticatedSync();
    if (!isAuth) return false;
    return !!this.getToken();
  }

  /** Allow host to respond to authentication changes */
  public get authenticationChanged(): BehaviorSubject<boolean> {
    return this.oktaWrapper.authenticationChanged;
  }

  // -------------------- Headers --------------------

  /**
   * Builds bearer auth headers, automatically appending the impersonation
   * header when an impersonation session is active.
   */
  public getHttpBearerHeaders(token: string): HttpHeaders {

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
      'Ocp-Apim-Subscription-Key': this.apiKey
    };

    const email = this.impersonation.get();
    if (email) {
      headers['Impersonating'] = email;
    }

    return new HttpHeaders(headers);
  }

  /**
   * Builds basic auth headers, automatically appending the impersonation
   * header when an impersonation session is active.
   */
  public getHttpBasicHeaders(basicToken: string): HttpHeaders {

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + basicToken,
      'Ocp-Apim-Subscription-Key': this.apiKey
    };

    const email = this.impersonation.get();
    if (email) {
      headers['Impersonating'] = email;
    }

    return new HttpHeaders(headers);
  }


  /**
   * Builds bearer auth headers WITHOUT Content-Type for multipart file uploads.
   * Content-Type must not be set explicitly — the browser sets multipart/form-data
   * with the correct boundary automatically.
   * Includes impersonation header when an impersonation session is active.
   */
  public getHttpUploadHeaders(token: string): HttpHeaders {

    const headers: Record<string, string> = {
      'Authorization': 'Bearer ' + token,
      'Ocp-Apim-Subscription-Key': this.apiKey
    };

    const email = this.impersonation.get();
    if (email) {
      headers['Impersonating'] = email;
    }

    return new HttpHeaders(headers);
  }


  public getHttpAnonymousHeaders(): HttpHeaders {

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': this.apiKey
    };

    return new HttpHeaders(headers);
  }

  // -------------------- Impersonation --------------------

  /** Returns the active impersonation email, or null if not impersonating. */
  public getImpersonatingEmail(): string | null | undefined {
    return this.impersonation.get();
  }

  /** Begins an impersonation session with the given email, optional display name, and the impersonated user's company id. */
  public setImpersonatingEmail(email?: string | null, name?: string | null, companyId?: string | null): void {
    this.impersonation.set(email, name, companyId);
  }

  /** Ends the current impersonation session. */
  public resetImpersonationEmail(): void {
    this.impersonation.reset();
  }

  // -------------------- URL helpers --------------------

  /** Prepends the configured base api URL to the given path. */
  public prependBaseApiUrl(path: string): string {
    return urlJoin(this.baseUrl, path);
  }

  // -------------------- HTTP helpers --------------------

  /** GET — untyped response. */
  protected getAny(url: string, httpHeaders: HttpHeaders | undefined): Observable<any> {
    if (httpHeaders) {
      return this.http.get(url, { headers: httpHeaders });
    }
    return this.http.get(url);
  }

  /** POST — untyped response. */
  protected postAny(content: any, url: string, httpHeaders: HttpHeaders | undefined): Observable<any> {
    if (httpHeaders) {
      return this.http.post(url, content, { headers: httpHeaders });
    }
    return this.http.post(url, content);
  }

  /** DELETE — untyped response. */
  protected deleteAny(url: string, httpHeaders: HttpHeaders): Observable<any> {
    if (httpHeaders) {
      return this.http.delete(url, { headers: httpHeaders });
    }
    return this.http.delete(url);
  }

  /** PUT — untyped response. */
  protected putAny(content: any, url: string, httpHeaders: HttpHeaders): Observable<any> {
    if (httpHeaders) {
      return this.http.put(url, content, { headers: httpHeaders });
    }
    return this.http.put(url, content);
  }

  /** GET — typed array response. */
  protected getItems<T>(url: string, httpHeaders: HttpHeaders): Observable<T[]> {
    if (httpHeaders) {
      return this.http.get<T[]>(url, { headers: httpHeaders });
    }
    return this.http.get<T[]>(url);
  }

  /** GET — single typed item response. */
  protected getItem<T>(url: string, httpHeaders: HttpHeaders): Observable<T> {
    if (httpHeaders) {
      return this.http.get<T>(url, { headers: httpHeaders });
    }
    return this.http.get<T>(url);
  }

  /** DELETE — returns boolean success. */
  protected removeItem<T>(_item: T, url: string, httpHeaders: HttpHeaders): Observable<boolean> {
    if (httpHeaders) {
      return this.http.delete<boolean>(url, { headers: httpHeaders });
    }
    return this.http.delete<boolean>(url);
  }

  /** POST (new) or PUT (existing) — returns boolean success. */
  protected saveItem<T>(item: T, isNew: boolean, url: string, httpHeaders?: HttpHeaders): Observable<boolean> {
    if (httpHeaders) {
      if (isNew) {
        return this.http.post<boolean>(url, item, { headers: httpHeaders });
      }
      return this.http.put<boolean>(url, item, { headers: httpHeaders });
    }
    return this.http.post<boolean>(url, item);
  }
}
